/**
 * Viewer — the actual radiology viewer surface (toolbar + series panel +
 * viewport grid + sidecar panels). Extracted from ViewerPage in v4.3 so it
 * can be rendered in three different shells:
 *
 *   - Modal (embedded over the worklist tab) — onClose closes the overlay
 *   - Single-tab multi-study viewer — onClose closes the tab
 *   - Per-study tab — onClose closes the tab (or navigates back)
 *
 * Routing-vs-prop split: ViewerPage handles `/viewer/:studyId` + `?series=`
 * URLs and forwards them as props here. The modal mode opens this directly
 * without going through the router. Either path uses the same multi-study
 * state, BroadcastChannel subscription, key-image polling, etc.
 */
import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useViewerStore } from '../stores/viewerStore';
import { apiCall } from '../api/client';
import { initCornerstone, StudyData, RENDERING_ENGINE_ID, cornerstoneTools } from '../cornerstone/init';
import { getRenderingEngine, cache as csCache } from '@cornerstonejs/core';
import ViewportGrid, { ViewportGridHandle } from './ViewportGrid';
import Toolbar from './Toolbar';
import SeriesPanel, { ReportStatus } from './SeriesPanel';
import FloatingControls from './FloatingControls';
import { StudyOpenCardSlot } from '../plugins/runtime/host/StudyOpenCardSlot';
import ViewportContextMenu from './ViewportContextMenu';
import ProtocolsDialog from './ProtocolsDialog';
import AiFindingsPanel from './AiFindingsPanel';
import KeyImageOverlay from './KeyImageOverlay';
import { eventTarget as csEventTarget, Enums as csCoreEnums } from '@cornerstonejs/core';
import { applyHangingProtocol, applyMatchedProtocol } from '../utils/hangingProtocols';
import {
  currentImageRefFromViewport, refsForSeries, sendToComposer,
  getEdgePrintInstallerUrl, launchCompanion,
} from '../utils/composerHandoff';
import CompanionLaunchDialog from './CompanionLaunchDialog';
import { getServerUrl } from '../api/client';
import { useAuthStore } from '../stores/authStore';
import { subscribeToAddStudy } from '../utils/viewerChannel';
import { getProvider, refreshProvider, hashUid } from '../data/providers';
import DataProgressBanner from './DataProgressBanner';
import {
  KeyImage, listKeyImages, toggleKeyImage, keyImageKey, keyImageSet,
} from '../utils/keyImages';
import type { PluginContext } from '../plugins';
import styles from '../pages/ViewerPage.module.css';

interface Props {
  /** Either an EDGEPACS numeric studyId (digit string) OR a DICOM
   *  StudyInstanceUID (1.2.3.4.5...). The component detects which form
   *  based on whether the string contains a dot and routes through the
   *  provider accordingly. */
  studyId: number | string;
  /** DICOMweb StudyInstanceUID explicitly. Optional in EDGEPACS mode.
   *  When set, takes precedence over the string parse of studyId. */
  studyInstanceUID?: string;
  requestedSeriesId?: number;
  /** Invoked when the user hits the Close Viewer button (or Esc, in modal mode) */
  onClose: () => void;
  /** True when rendered as a modal overlay over the worklist. Affects:
   *   - the "Back" path returned to from the report editor
   *   - whether we mutate document.title (modal shouldn't, the worklist tab
   *     should keep its title) */
  embedded?: boolean;
}

export default function Viewer({
  studyId, studyInstanceUID, requestedSeriesId, onClose, embedded = false,
}: Props) {
  const navigate = useNavigate();
  const [studies, setStudies] = useState<StudyData[]>([]);
  const [activeStudyId, setActiveStudyId] = useState<number | null>(null);
  const studyData = useMemo<StudyData | null>(
    () => studies.find((s) => s.studyId === activeStudyId) ?? studies[0] ?? null,
    [studies, activeStudyId]
  );

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [csReady, setCsReady] = useState(false);
  /** True when the error screen is showing PdfViewport as a fallback
   *  (user clicked "Try as PDF"). Bypasses the regular viewer entirely. */
  const [pdfFallbackUrl, setPdfFallbackUrl] = useState<string | null>(null);
  const [loadedIds, setLoadedIds] = useState<Set<string>>(new Set());
  const [activeSeriesId, setActiveSeriesId] = useState<number | null>(null);
  const [activeViewportIndex, setActiveViewportIndex] = useState<number>(0);
  const [showProtocols, setShowProtocols] = useState(false);
  /** Node rendered by plugin toolbar buttons via PluginContext.showModal. */
  const [pluginModal, setPluginModal] = useState<React.ReactNode | null>(null);
  const [reportStatus, setReportStatus] = useState<ReportStatus>('none');
  const [keyImages, setKeyImages] = useState<KeyImage[]>([]);
  const [currentImgKey, setCurrentImgKey] = useState<string>('');
  const [toast, setToast] = useState<string>('');
  // Install-dialog state. Set ONLY when launchCompanion() detects the
  // Companion isn't installed on this machine (no window blur fired
  // within its timeout). Machines that already have the Companion see
  // nothing when they click - the app just opens.
  const [launchingMsg, setLaunchingMsg] = useState<string | null>(null);
  // Retained across renders so the dialog's "Retry launch" button can
  // re-fire the exact deep link the user originally clicked.
  const [lastLaunchAction, setLastLaunchAction] = useState<(() => boolean) | null>(null);
  const layout = useViewerStore((s) => s.layout);

  useEffect(() => {
    const [r, c] = layout.split('x').map(Number);
    const count = r * c;
    if (activeViewportIndex >= count) setActiveViewportIndex(0);
  }, [layout, activeViewportIndex]);

  // Reset per-viewport mode overrides AND layout whenever the active
  // study changes. Otherwise a study opened in MPR (or an MG that HP
  // put in 2x2) leaves the next study inheriting that state. Combined
  // with the key={activeStudyId} remount below, this gives a fully
  // fresh viewport for each new study. Any HP that runs on the new
  // study (via addStudy → applyHangingProtocol) fires ~300ms after
  // this reset and correctly overrides layout back to the HP's value.
  //
  // SAFETY: also purge the Cornerstone singleton image + volume cache.
  // Cache retention across studies is a patient-safety hazard — MG images
  // could composite into a subsequent CT viewport because their imageIds
  // were still resident in cache when the fresh ViewportGrid mounted.
  // See issue #4 (2026-08-19): closing a study and opening another one
  // showed the first study's images in the new viewport.
  useEffect(() => {
    if (activeStudyId != null) {
      useViewerStore.getState().clearViewportOverrides();
      // Also reset layout — clearViewportOverrides intentionally leaves
      // layout alone for other callers (e.g. viewer-mode switches within
      // the same study). Study switch should always start at 1x1 unless
      // an HP overrides.
      useViewerStore.getState().setLayout('1x1');
      try {
        csCache.purgeCache();
      } catch (err) {
        console.warn('[Viewer] csCache.purgeCache on study switch failed:', err);
      }
    }
  }, [activeStudyId]);

  // Also purge on viewer unmount so nothing lingers between viewer sessions
  // (modal close, tab close). Runs once when Viewer tears down.
  useEffect(() => {
    return () => {
      try {
        csCache.purgeCache();
      } catch { /* best-effort */ }
    };
  }, []);

  const gridRef = useRef<ViewportGridHandle>(null);
  const protocolAppliedRef = useRef(false);
  const toggleKeyImageRef = useRef<() => void>(() => {});

  const keyImageKeys = keyImageSet(keyImages);
  const isCurrentImageKey = currentImgKey !== '' && keyImageKeys.has(currentImgKey);

  useEffect(() => {
    console.log(`[Viewer] mount studyId=${studyId} embedded=${embedded}`);
    if (!embedded) document.title = 'Viewer';
    initCornerstone().then(() => {
      console.log('[Viewer] Cornerstone ready');
      setCsReady(true);
    });
    return () => console.log(`[Viewer] unmount studyId=${studyId}`);
  }, [embedded]);

  // Diagnostic — surface what Cornerstone's image loader is doing.
  // Lets us see whether image bytes are being requested (and for which
  // imageId) even when our own load paths weren't invoked. Catches the
  // "Cornerstone tries to decode something we didn't ask it to" class
  // of bug that has been hiding the real source of the PDF crash.
  useEffect(() => {
    function onLoadFailed(e: any) {
      // Cornerstone3D fires this event with TWO different detail shapes:
      //   * Singular:  { imageId, error }                              (StackViewport.js:1544, BaseStreamingImageVolume.js:163)
      //   * Array:     [ { status: 'rejected', reason: Error }, ... ]  (StackViewport.js:1571 — from Promise.allSettled)
      // Handling only the singular shape used to log "undefined undefined"
      // for the array-shape events, hiding the real error from the console.
      const d = e?.detail;
      if (Array.isArray(d)) {
        for (const item of d) {
          const err = item?.reason;
          console.error('[CS] image_load_failed',
            item?.imageId ?? '(no imageId in array item)',
            err?.message || err || '(no error info)',
            err?.stack ? '\n' + err.stack : '');
        }
      } else {
        console.error('[CS] image_load_failed',
          d?.imageId ?? '(no imageId)',
          d?.error?.message || d?.error || '(no error info)',
          d?.error?.stack ? '\n' + d.error.stack : '');
      }
    }
    // IMAGE_LOADED fires for every slice — silence unless we're
    // explicitly debugging (window.__edgewebDebugImageLoad = true).
    function onLoaded(e: any) {
      if ((window as any).__edgewebDebugImageLoad) {
        console.log('[CS] image_loaded', e?.detail?.image?.imageId);
      }
    }
    csEventTarget.addEventListener(csCoreEnums.Events.IMAGE_LOAD_ERROR, onLoadFailed);
    csEventTarget.addEventListener(csCoreEnums.Events.IMAGE_LOADED, onLoaded);
    return () => {
      csEventTarget.removeEventListener(csCoreEnums.Events.IMAGE_LOAD_ERROR, onLoadFailed);
      csEventTarget.removeEventListener(csCoreEnums.Events.IMAGE_LOADED, onLoaded);
    };
  }, []);

  // Global window error handler — Cornerstone's worker errors bubble to
  // window.onerror, not to React's error boundary. Without this, the
  // viewer becomes unresponsive after a decode failure with no clue
  // surfaced to the user. We log it, swallow (preventDefault), AND ask
  // ViewportGrid to fall back to PDF rendering for the loaded series —
  // by the time we get here the file has already failed image decode,
  // so PDF is the next reasonable guess (and PdfViewport's own error
  // UI handles the case where it isn't a PDF either).
  useEffect(() => {
    function onError(e: ErrorEvent) {
      const msg = e.error?.message || e.message || '';
      if (msg.includes("Cannot read properties of undefined (reading 'map')")
          || msg.includes('samplesPerPixel')) {
        console.error('[Viewer] Cornerstone decode failed, falling back to PDF rendering:', msg);
        e.preventDefault();
        // Defer to next tick so React has finished the in-flight render.
        setTimeout(() => gridRef.current?.fallbackAllSeriesToPdf(), 0);
        return true;
      }
    }
    window.addEventListener('error', onError);
    return () => window.removeEventListener('error', onError);
  }, []);

  const addStudy = useCallback(
    async (id: number, requestedSeries?: number, studyUID?: string): Promise<boolean> => {
      let alreadyLoaded = false;
      let wasFirst = false;
      setStudies((prev) => {
        if (prev.some((s) => s.studyId === id)) {
          alreadyLoaded = true;
          return prev;
        }
        wasFirst = prev.length === 0;
        return prev;
      });
      if (alreadyLoaded) {
        setActiveStudyId(id);
        return true;
      }

      try {
        // Provider routes to EDGEPACS or DICOMweb based on Settings.
        // For DICOMweb, the StudyInstanceUID is required (id is a hash).
        await refreshProvider();
        const provider = getProvider();
        console.log(`[Viewer] addStudy: calling provider.getStudy id=${id} uid=${studyUID || '-'} kind=${provider.kind}`);
        const newStudy: StudyData = await provider.getStudy(id, studyUID);
        console.log(`[Viewer] addStudy: got study`, {
          studyId: newStudy.studyId,
          patientName: newStudy.patientName,
          modality: newStudy.modality,
          seriesCount: newStudy.series?.length,
          seriesIsArray: Array.isArray(newStudy.series),
        });
        if (newStudy.series) {
          newStudy.series.sort((a: any, b: any) =>
            (parseInt(a.seriesNumber, 10) || 0) - (parseInt(b.seriesNumber, 10) || 0)
          );
        }
        let initialSeriesId = newStudy.series?.[0]?.seriesId;
        if (requestedSeries && newStudy.series) {
          const idx = newStudy.series.findIndex((s: any) => s.seriesId === requestedSeries);
          if (idx > 0) {
            const [target] = newStudy.series.splice(idx, 1);
            newStudy.series.unshift(target);
            initialSeriesId = target.seriesId;
          } else if (idx === 0) {
            initialSeriesId = requestedSeries;
          }
        }

        setStudies((prev) => [...prev, newStudy]);
        setActiveStudyId(newStudy.studyId);
        if (initialSeriesId != null) setActiveSeriesId(initialSeriesId);

        if (!wasFirst && initialSeriesId != null) {
          setTimeout(() => {
            gridRef.current?.loadSeries(initialSeriesId!, activeViewportIndex);
          }, 200);
        }

        if (wasFirst && !requestedSeries && !protocolAppliedRef.current
            && provider.supports('reports')) {
          // Hanging protocols are EDGEPACS-only for now (server-side
          // matching against hanging_protocols table); skip silently in
          // DICOMweb mode.
          protocolAppliedRef.current = true;
          setTimeout(() => {
            // Feedback: HP orchestration triggers parallel image loads —
            // 4-view MG can be 30-40 MB of downloads. Show the user
            // something is happening rather than a black grid.
            setToast('Applying hanging protocol…');
            applyHangingProtocol(newStudy, gridRef.current)
              .then((applied) => {
                if (!applied) setToast('');
                // If applied, per-viewport progress bars take over — the
                // toast auto-hides after 1.8s from the existing useEffect.
              })
              .catch(() => setToast(''));
          }, 50);
        }
        return true;
      } catch (e: any) {
        setError(e?.message || 'Unable to reach the server. Please check your connection.');
        return false;
      }
    },
    [activeViewportIndex]
  );

  // Detect whether the studyId arg is a numeric EDGEPACS id or a DICOM UID
  // (UIDs always contain dots). Hash the UID to a stable numeric id for
  // React keying so the existing seriesId-keyed state doesn't need to know.
  const { numericId, uidFromProp } = useMemo(() => {
    const raw = String(studyId);
    if (studyInstanceUID || raw.includes('.')) {
      const uid = studyInstanceUID || raw;
      return { numericId: hashUid(uid), uidFromProp: uid };
    }
    return { numericId: Number(raw) || 0, uidFromProp: undefined as string | undefined };
  }, [studyId, studyInstanceUID]);

  // Initial study load
  useEffect(() => {
    if (!numericId || !csReady) return;
    setLoading(true);
    addStudy(numericId, requestedSeriesId, uidFromProp)
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numericId, csReady]);

  // Subscribe to add-study requests (modal: local bus; tabbed: BroadcastChannel).
  // Today the channel only carries numeric ids — DICOMweb cross-tab opens
  // would need the UID propagated too (Phase 2 work).
  useEffect(() => {
    if (!csReady) return;
    return subscribeToAddStudy(async (id, seriesId) => {
      await addStudy(id, seriesId);
    });
  }, [csReady, addStudy]);

  // Keyboard shortcuts
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if ((e.target as HTMLElement)?.tagName === 'INPUT' || (e.target as HTMLElement)?.tagName === 'SELECT') return;

      const { setActiveTool, toggleOverlays } = useViewerStore.getState();

      switch (e.key.toLowerCase()) {
        case 'w': setActiveTool('Wwwc'); break;
        case 'p': setActiveTool('Pan'); break;
        case 'z': setActiveTool('Zoom'); break;
        case 's': setActiveTool('StackScroll'); break;
        case 'l': setActiveTool('Length'); break;
        case 'a': setActiveTool('Angle'); break;
        case 'm': setActiveTool('Magnify'); break;
        case 'r':
          {
            const engine = getRenderingEngine(RENDERING_ENGINE_ID);
            const vp = engine?.getViewport('viewport-0');
            if (vp) { vp.resetCamera(); (vp as any).resetProperties?.(); vp.render(); }
          }
          break;
        case 'i':
          {
            const engine = getRenderingEngine(RENDERING_ENGINE_ID);
            const vp = engine?.getViewport('viewport-0');
            if (vp) {
              const { invert } = vp.getProperties();
              (vp as any).setProperties({ invert: !invert });
              vp.render();
            }
          }
          break;
        case 'o': toggleOverlays(); break;
        case 'k':
          toggleKeyImageRef.current();
          break;
        // Esc handled by the modal shell (WorklistPage) when embedded; in
        // standalone tabs we deliberately don't bind it — accidental close
        // mid-read is too easy.
        case 'arrowup':
        case 'arrowleft':
          e.preventDefault();
          {
            const engine = getRenderingEngine(RENDERING_ENGINE_ID);
            const vp = engine?.getViewport('viewport-0') as any;
            if (vp?.getCurrentImageIdIndex && vp?.setImageIdIndex) {
              const idx = vp.getCurrentImageIdIndex();
              if (idx > 0) vp.setImageIdIndex(idx - 1);
            }
          }
          break;
        case 'arrowdown':
        case 'arrowright':
          e.preventDefault();
          {
            const engine = getRenderingEngine(RENDERING_ENGINE_ID);
            const vp = engine?.getViewport('viewport-0') as any;
            if (vp?.getCurrentImageIdIndex && vp?.setImageIdIndex) {
              const idx = vp.getCurrentImageIdIndex();
              const total = vp.getImageIds?.()?.length ?? 0;
              if (idx < total - 1) vp.setImageIdIndex(idx + 1);
            }
          }
          break;
        case 'home':
          e.preventDefault();
          {
            const engine = getRenderingEngine(RENDERING_ENGINE_ID);
            const vp = engine?.getViewport('viewport-0') as any;
            if (vp?.setImageIdIndex) vp.setImageIdIndex(0);
          }
          break;
        case 'end':
          e.preventDefault();
          {
            const engine = getRenderingEngine(RENDERING_ENGINE_ID);
            const vp = engine?.getViewport('viewport-0') as any;
            if (vp?.setImageIdIndex && vp?.getImageIds) {
              vp.setImageIdIndex(vp.getImageIds().length - 1);
            }
          }
          break;
      }
    }

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  const handleLoadedIdsChange = useCallback((ids: Set<string>) => {
    setLoadedIds(ids);
  }, []);

  const refreshReportStatus = useCallback(async (id?: number) => {
    const sId = id ?? studyData?.studyId;
    if (!sId) return;
    try {
      const res = await apiCall<{ content: string; status: string }>('reports.get', { studyId: sId });
      if (res.s === 'ok' && res.data?.[0]) {
        const s = (res.data[0].status as ReportStatus) || 'draft';
        setReportStatus(s);
      } else {
        setReportStatus('none');
      }
    } catch { /* ignore */ }
  }, [studyData?.studyId]);

  useEffect(() => {
    if (studyData?.studyId) refreshReportStatus(studyData.studyId);
  }, [studyData?.studyId, refreshReportStatus]);

  useEffect(() => {
    function onStorage(e: StorageEvent) {
      if (!studyData?.studyId) return;
      if (e.key !== `edgeweb-report-status-${studyData.studyId}`) return;
      const raw = (e.newValue || '').split('|')[0] as ReportStatus;
      const val: ReportStatus = raw === 'draft' || raw === 'signed' ? raw : 'none';
      setReportStatus(val);
    }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [studyData?.studyId]);

  useEffect(() => {
    function onFocus() { refreshReportStatus(); }
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refreshReportStatus]);

  function handleSeriesClick(seriesId: number) {
    setActiveSeriesId(seriesId);
    gridRef.current?.loadSeries(seriesId, activeViewportIndex);
  }

  useEffect(() => {
    if (!studyData?.studyId) return;
    listKeyImages(studyData.studyId).then(setKeyImages);
  }, [studyData?.studyId]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(''), 1800);
    return () => clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    if (!studyData) return;
    const timer = setInterval(() => {
      const img = gridRef.current?.getCurrentImage();
      if (img) {
        const k = keyImageKey(img.seriesId, img.imageIndex);
        setCurrentImgKey((prev) => prev === k ? prev : k);
      }
    }, 300);
    return () => clearInterval(timer);
  }, [studyData]);

  async function handleToggleKeyImage() {
    const img = gridRef.current?.getCurrentImage();
    if (!img || !studyData) return;
    const result = await toggleKeyImage(studyData.studyId, img.seriesId, img.imageIndex);
    if (!result) return;
    if (result.removed) {
      setKeyImages((prev) => prev.filter((k) =>
        !(k.seriesId === img.seriesId && k.imageIndex === img.imageIndex)));
      setToast('Removed from key images');
    } else {
      setKeyImages((prev) => [...prev, {
        keyImageId: result.keyImageId,
        seriesId: img.seriesId,
        imageIndex: img.imageIndex,
        userId: 0,
        note: '',
        createdAt: new Date().toISOString(),
      }]);
      setToast('★ Marked as key image');
    }
  }

  toggleKeyImageRef.current = handleToggleKeyImage;

  // ---------- Composer basket ----------
  const composerBasket = useViewerStore((s) => s.composerBasket);
  const addToComposerBasket = useViewerStore((s) => s.addToComposerBasket);
  const clearComposerBasket = useViewerStore((s) => s.clearComposerBasket);

  // Feature-gated: the composer handoff is still in preview. Only surface the
  // Add / Push buttons for the dev account until the desktop side ships.
  // Case-insensitive so "Sriram" / "SRIRAM" both work.
  const currentUsername = useAuthStore((s) => s.username);
  const canUseComposer = currentUsername?.toLowerCase() === 'sriram';

  function handleAddToComposerBasket() {
    // Grab the imageRef of whatever's currently shown in the active viewport.
    // If the active viewport has no image (empty layout cell), silently no-op.
    const ref = currentImageRefFromViewport(activeViewportIndex);
    if (!ref) {
      setToast('No image in the active viewport to add.');
      return;
    }
    const wasNew = !composerBasket.includes(ref);
    addToComposerBasket(ref);
    setToast(wasNew
      ? `Added to Composer basket (${composerBasket.length + 1})`
      : 'Already in Composer basket');
  }

  function handleAddSeriesToComposerBasket(seriesId: number) {
    // Add every image of the series in order. Dedup logic in the store
    // handles the case where some slices were already added individually.
    const series = studies.flatMap((s) => s.series).find((s) => s.seriesId === seriesId);
    if (!series) return;
    const refs = refsForSeries(seriesId, series.numImages);
    addToComposerBasket(refs);
    setToast(`Added series (${refs.length}) to Composer basket`);
  }

  async function handleSendComposerBasket() {
    if (!studyData) return;
    // Empty basket = "just open the composer" — user can drop images later
    // (via key images, or by coming back and hitting Add + Send again).
    const msg = composerBasket.length === 0
      ? 'Launching Composer'
      : `Sending ${composerBasket.length} image(s) to Composer`;
    const fire = () => sendToComposer(
      studyData.studyId,
      composerBasket,
      getServerUrl(),
      useAuthStore.getState().acctoken,
    );
    setLastLaunchAction(() => fire);
    // launchCompanion() detects (via window blur within ~2.5s) whether
    // the Companion actually took focus. If it did, silent success. If
    // not, we surface the install dialog - no more "close me" toast
    // for the happy-path users who already have the Companion.
    const outcome = await launchCompanion(fire);
    if (outcome === 'not_installed') setLaunchingMsg(msg);
  }

  function handleClearComposerBasket() {
    clearComposerBasket();
    setToast('Composer basket cleared.');
  }

  async function handleDeleteKeyImage(k: KeyImage) {
    if (!studyData) return;
    const result = await toggleKeyImage(studyData.studyId, k.seriesId, k.imageIndex);
    if (result?.removed) {
      setKeyImages((prev) => prev.filter((x) => x.keyImageId !== k.keyImageId));
    }
  }

  function handleJumpToKeyImage(k: KeyImage) {
    setActiveSeriesId(k.seriesId);
    gridRef.current?.jumpToImage(k.seriesId, k.imageIndex);
  }

  function handleOpenReport() {
    if (!studyData) return;
    // In modal mode, "back" returns to the worklist (since that's where we
    // came from). Otherwise it returns to this viewer's URL. We also close
    // the modal first so the report doesn't render under it.
    if (embedded) {
      onClose();
      navigate(`/report/${studyData.studyId}`, {
        state: { from: '/worklist', fromLabel: 'Worklist' },
      });
    } else {
      navigate(`/report/${studyData.studyId}`, {
        state: { from: `/viewer/${studyData.studyId}`, fromLabel: 'Viewer' },
      });
    }
  }

  function handleCloseStudy(closingStudyId: number) {
    if (studies.length <= 1) {
      setToast('Cannot close the last study — close the viewer to exit.');
      return;
    }
    gridRef.current?.releaseStudy(closingStudyId);
    const remaining = studies.filter((s) => s.studyId !== closingStudyId);
    setStudies(remaining);
    if (activeStudyId === closingStudyId) {
      setActiveStudyId(remaining[0]?.studyId ?? null);
    }
  }

  const getActiveViewportElement = useCallback((): HTMLDivElement | null => {
    return document.getElementById('viewport-0') as HTMLDivElement | null;
  }, []);

  /** Handed to plugin toolbar buttons so they can open modals and reach
   *  the active viewport without importing Cornerstone directly. */
  const pluginCtx = useMemo<PluginContext>(() => ({
    getActiveViewport: () => getRenderingEngine(RENDERING_ENGINE_ID)?.getViewport('viewport-0') ?? null,
    getActiveViewportElement,
    getStudyContext: () => {
      if (!studyData) return null;
      const activeSeries = activeSeriesId
        ? studyData.series.find((s) => s.seriesId === activeSeriesId)
        : studyData.series[0];
      return {
        modality: activeSeries?.modality || studyData.modality,
        bodyPart: activeSeries?.bodyPart,
        studyDescription: studyData.studyDescription,
        seriesDescription: activeSeries?.seriesDescription,
      };
    },
    showModal: (node) => setPluginModal(node),
    hideModal: () => setPluginModal(null),
  }), [getActiveViewportElement, studyData, activeSeriesId]);

  if (loading) {
    return (
      <div className={styles.loading}>
        <div className={styles.loadingSpinner} />
        <div>Loading study…</div>
      </div>
    );
  }

  if (error) {
    // PDF fallback overlay — Viewer-level so it doesn't depend on
    // ViewportGrid having mounted. Triggered by "Try as PDF" on the
    // error screen.
    if (pdfFallbackUrl) {
      return (
        <iframe
          src={pdfFallbackUrl}
          style={{ position: 'fixed', inset: 0, border: 'none', background: 'white', width: '100%', height: '100%' }}
          title="DICOM Encapsulated PDF (fallback)"
        />
      );
    }
    async function tryAsPdf() {
      // We don't have a StudyData object here (the fetch failed before
      // setStudies ran), so re-fetch the study to get its first series,
      // then ask the provider for a PDF blob URL.
      try {
        const provider = getProvider();
        const newStudy = await provider.getStudy(numericId, uidFromProp);
        const firstSeries = newStudy.series?.[0];
        if (!firstSeries) throw new Error('Study has no series.');
        const url = await provider.getPdfBlobUrl(newStudy, firstSeries);
        setPdfFallbackUrl(url);
      } catch (e: any) {
        setError(`PDF fallback also failed: ${e?.message || e}`);
      }
    }
    return (
      <div className={styles.loading}>
        <div>{error}</div>
        <div style={{ marginTop: 16, display: 'flex', gap: 12 }}>
          <button onClick={tryAsPdf}>Try as PDF</button>
          <button onClick={onClose}>Close Viewer</button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <Toolbar
        onBack={onClose}
        studyId={studyData?.studyId}
        modality={studyData?.modality}
        onOpenProtocols={() => setShowProtocols(true)}
        onToggleKeyImage={handleToggleKeyImage}
        isCurrentImageKey={isCurrentImageKey}
        pluginCtx={pluginCtx}
        {...(canUseComposer ? {
          onAddToComposerBasket: handleAddToComposerBasket,
          onSendComposerBasket: handleSendComposerBasket,
          onClearComposerBasket: handleClearComposerBasket,
          composerBasketCount: composerBasket.length,
        } : {})}
      />
      <DataProgressBanner />
      <div className={styles.main}>
        <SeriesPanel
          studies={studies}
          activeStudyId={activeStudyId}
          onActiveStudyChange={setActiveStudyId}
          onCloseStudy={handleCloseStudy}
          loadedIds={loadedIds}
          onSeriesClick={handleSeriesClick}
          activeSeriesId={activeSeriesId}
          reportStatus={reportStatus}
          onOpenReport={handleOpenReport}
          keyImages={keyImages}
          onJumpKeyImage={handleJumpToKeyImage}
          onDeleteKeyImage={handleDeleteKeyImage}
          onAddSeriesToComposerBasket={handleAddSeriesToComposerBasket}
        />
        <ViewportGrid
          key={activeStudyId ?? 'no-study'}
          ref={gridRef}
          studies={studies}
          activeStudyId={activeStudyId}
          layout={layout}
          onLoadedIdsChange={handleLoadedIdsChange}
          activeViewportIndex={activeViewportIndex}
          onActiveViewportChange={setActiveViewportIndex}
        />
        <AiFindingsPanel
          studyId={studyData?.studyId ?? null}
          onJump={(seriesId, imageIndex) => {
            setActiveSeriesId(seriesId);
            gridRef.current?.jumpToImage(seriesId, imageIndex);
          }}
        />
      </div>
      <KeyImageOverlay visible={isCurrentImageKey} />
      {toast && <div className={styles.toast}>{toast}</div>}

      {/* Install-Companion dialog. Only rendered when launchCompanion()
          detected the deep link failed - so machines with the Companion
          already installed never see this. */}
      {launchingMsg && (
        <CompanionLaunchDialog
          action={launchingMsg}
          installerUrl={getEdgePrintInstallerUrl(getServerUrl())}
          onClose={() => setLaunchingMsg(null)}
          onRetry={lastLaunchAction ?? undefined}
        />
      )}

      {/* Plugin-mounted modal (e.g. chiro tool picker + wizard). Plugins
          own the visibility via PluginContext.showModal/hideModal so
          each plugin can express its own flow without coupling to
          Viewer state. */}
      {pluginModal}

      {showProtocols && studyData && (
        <ProtocolsDialog
          studyData={studyData}
          onClose={() => setShowProtocols(false)}
          onApply={(protocol) => {
            // Fully apply the protocol: set layout AND populate each
            // viewport per the config. Previously only the layout was
            // set, so clicking "Apply" on a specific HP left viewports
            // empty (looked like nothing happened).
            applyMatchedProtocol(protocol, studyData, gridRef.current)
              .catch((e) => console.warn('[HP] manual apply failed:', e));
          }}
        />
      )}
      <FloatingControls getActiveViewportElement={getActiveViewportElement} />
      {/* Runtime plugin cards (LIRA zone + score + action, future vendors
          stack beneath). Positioned in the top-right so they overlay the
          image without blocking the toolbar. Only renders if a plugin
          declared the 'study_open_card' slot. */}
      {activeStudyId != null && (
        <div style={{ position: 'absolute', top: 60, right: 10, maxWidth: 320, zIndex: 50 }}>
          <StudyOpenCardSlot studyId={String(activeStudyId)} />
        </div>
      )}
      <ViewportContextMenu
        onSelectTool={(tool) => useViewerStore.getState().setActiveTool(tool)}
        onInvert={() => {
          const vp = getRenderingEngine(RENDERING_ENGINE_ID)?.getViewport('viewport-0');
          if (vp) { const { invert } = vp.getProperties(); (vp as any).setProperties({ invert: !invert }); vp.render(); }
        }}
        onFlipH={() => {
          const vp = getRenderingEngine(RENDERING_ENGINE_ID)?.getViewport('viewport-0');
          if (vp) { const { flipHorizontal } = vp.getCamera(); vp.setCamera({ flipHorizontal: !flipHorizontal }); vp.render(); }
        }}
        onFlipV={() => {
          const vp = getRenderingEngine(RENDERING_ENGINE_ID)?.getViewport('viewport-0');
          if (vp) { const { flipVertical } = vp.getCamera(); vp.setCamera({ flipVertical: !flipVertical }); vp.render(); }
        }}
        onRotateL={() => {
          const vp = getRenderingEngine(RENDERING_ENGINE_ID)?.getViewport('viewport-0') as any;
          if (vp?.setRotation) { vp.setRotation((vp.getRotation?.() ?? 0) - 90); vp.render(); }
        }}
        onRotateR={() => {
          const vp = getRenderingEngine(RENDERING_ENGINE_ID)?.getViewport('viewport-0') as any;
          if (vp?.setRotation) { vp.setRotation((vp.getRotation?.() ?? 0) + 90); vp.render(); }
        }}
        onReset={() => {
          const vp = getRenderingEngine(RENDERING_ENGINE_ID)?.getViewport('viewport-0');
          if (vp) { vp.resetCamera(); (vp as any).resetProperties?.(); vp.render(); }
        }}
        onClear={() => {
          cornerstoneTools.annotation.state.removeAllAnnotations();
          const vp = getRenderingEngine(RENDERING_ENGINE_ID)?.getViewport('viewport-0');
          vp?.render();
        }}
      />
    </div>
  );
}
